import { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'

import { db } from '../FirebaseInit'
import { useToast } from '../context/ToastContext'

/**
 * useTicketMessages
 *
 * Subscribes to a ticket's `messages` subcollection ordered by `createdAt`
 * ascending. Customers additionally constrain the query to non-internal
 * messages (`internal == false`) so the client query matches what the
 * Firestore security rules permit; admins read every message.
 *
 * @param {string} ticketId The parent ticket document id.
 * @param {Object} params
 * @param {boolean} params.admin Whether the current user is an admin.
 * @returns {{ data: Array, loading: boolean, error: (Error|null) }}
 */
const useTicketMessages = (ticketId, { admin }) => {
  const { showToast } = useToast()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!ticketId) {
      setData([])
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)

    const messagesRef = collection(db, 'tickets', ticketId, 'messages')
    const messagesQuery = admin
      ? query(messagesRef, orderBy('createdAt', 'asc'))
      : query(messagesRef, where('internal', '==', false), orderBy('createdAt', 'asc'))

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        setData(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
        setError(null)
        setLoading(false)
      },
      (err) => {
        if (err.code === 'permission-denied') {
          showToast('You don\'t have access to this ticket', 'red')
        }
        setError(err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [ticketId, admin, showToast])

  return { data, loading, error }
}

export default useTicketMessages
